import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import { TranslateService } from '@ngx-translate/core';
import { SalesTeamService } from 'src/app/core/timeline/_services/sales-team.service';
import { tap } from 'rxjs/operators';
import { UserService } from 'src/app/core/auth/_services';
import { LayoutUtilsService, MessageType } from 'src/app/core/_base/crud';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { SalesTeam } from '../../../../../../core/timeline/_models/sales-team.model';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatSelectChange } from '@angular/material/select';
import { AffiliationsService } from '../../../../../../core/lists';
import { SpecialtyService } from '../../../../../../core/lists/_services/specialty.service';
import { clone } from 'lodash-es';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-sales-team-edit',
  templateUrl: './sales-team-edit.component.html',
  styleUrls: ['./sales-team-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesTeamEditComponent implements OnInit, OnDestroy, AfterViewInit {
  salesTeam: SalesTeam;
  companyId: number;
  selectedTab = 0;
  loadingSubject = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean>;
  salesTeamForm: FormGroup;
  hasFormErrors = false;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  subscriptions: any[] = [];
  clicked = false;
  filtersJson = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];
  zipCtrl = new FormControl('');
  stateCtrl = new FormControl('');
  affiliationCtrl = new FormControl('');
  specialitiesCtrl = new FormControl('');
  @ViewChild('affiliationAutocomplete') affiliationAutocomplete: GeneralAutocompleteComponent;
  zips: string[] = [];
  zipValue;
  valueInputType = 'ZIP_CODE';
  @ViewChild('zipInput') zipInput: ElementRef<HTMLInputElement>;
  @ViewChild('organizerAutocomplete') organizerAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('membersAutocomplete') membersAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('wizard', { static: true }) el: ElementRef;
  required = true;
  filterIncorrect = false;
  affiliationAutocompleteCall = this.affiliationService.searchAffiliations.bind(
    this.affiliationService,
  );
  specialtiesAutocompleteCall = this.specialtyService.searchProviderTypes.bind(
    this.specialtyService,
  );
  @ViewChild('specialtiesAutocomplete') specialtiesAutocomplete: GeneralAutocompleteComponent;
  specialitiesObjects = [];
  affiliationsObjects = [];
  loadMembers = (a) => {
    return this.userService.userAutocomplete(a).pipe(
      tap(
        (data) =>
          (data.data = data.data.map((val: any) => {
            return {
              id: val.id,
              name: val.fullName,
            };
          })),
      ),
    );
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<SalesTeamEditComponent>,
    private userService: UserService,
    private store: Store<AppState>,
    private router: Router,
    private salesTeamFB: FormBuilder,
    private salesTeamService: SalesTeamService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    private layoutUtilsService: LayoutUtilsService,
    public stringUtils: StringUtilsService,
    private affiliationService: AffiliationsService,
    private specialtyService: SpecialtyService,
  ) {}

  ngOnInit() {
    if (this.data && this.data.salesTeam) {
      this.salesTeam = this.data.salesTeam;
    }
    this.loading$ = this.loadingSubject.asObservable();
    this.loadingSubject.next(true);
    this.createForm();
    this.loadingSubject.next(false);
  }

  ngAfterViewInit(): void {
    const wizard = new KTWizard(this.el.nativeElement, {
      startStep: 1,
    });
    wizard.on('beforeNext', (wizardObj) => {
      if (this.checkRequired()) {
        wizardObj.stop();
      }
    });

    // Change event
    wizard.on('change', () => {
      setTimeout(() => {
        KTUtil.scrollTop();
      }, 500);
    });

    if (this.salesTeam && this.salesTeam.manager)
      this.organizerAutocomplete.setValue(this.salesTeam.manager);
    if (this.salesTeam && this.salesTeam.members)
      this.membersAutocomplete.setValue(this.salesTeam.members);
  }

  checkRequired() {
    this.hasFormErrors = false;
    const controls = this.salesTeamForm.controls;
    if (this.salesTeamForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.cdr.detectChanges();
      this.hasFormErrors = true;
      return true;
    }
    return false;
  }

  getTitle() {
    if (this) return 'Create Sales Team';
  }

  ngOnDestroy() {
    this.cdr.detach();
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  createForm() {
    this.salesTeamForm = this.salesTeamFB.group({
      name: [this.salesTeam ? this.salesTeam.name : null, Validators.required],
      manager: [this.salesTeam ? this.salesTeam.manager : null],
      calls_on: [this.salesTeam ? this.salesTeam.calls_on : ''],
      type: [this.salesTeam ? this.salesTeam.type : ''],
      members: [this.salesTeam ? this.salesTeam.members : []],
      filterType: [],
      filterCriteria: [],
    });

    if (this.salesTeam && this.salesTeam.filters) {
      let allSpecialitiesInAllFilters = [];
      let allAffiliationsInAllFilters = [];
      let newFiltersMapped = clone(this.salesTeam.filters);
      newFiltersMapped.forEach((filter) => {
        if (filter.criteria === 'SPECIALTY') {
          allSpecialitiesInAllFilters = allSpecialitiesInAllFilters.concat(filter.values);
        }
        if (filter.criteria === 'AFFILIATION') {
          allAffiliationsInAllFilters = allAffiliationsInAllFilters.concat(filter.values);
        }
      });
      forkJoin({
        requestOne:
          allSpecialitiesInAllFilters.length > 0
            ? this.specialtyService.getProviderTypesByIds(allSpecialitiesInAllFilters)
            : of([]),
        requestTwo:
          allAffiliationsInAllFilters.length > 0
            ? this.affiliationService.getAffiliationsByIds(allAffiliationsInAllFilters)
            : of([]),
      }).subscribe(({ requestOne, requestTwo }) => {
        if (requestOne.data) {
          requestOne.data.forEach((specialty) => {
            this.specialitiesObjects[specialty.id] = specialty;
          });
          newFiltersMapped = newFiltersMapped.map((filter) => {
            if (filter.criteria === 'SPECIALTY') {
              return {
                type: filter.type,
                criteria: filter.criteria,
                values: filter.values.map((val) => {
                  return this.specialitiesObjects[val];
                }),
              };
            }
            return {
              type: filter.type,
              criteria: filter.criteria,
              values: filter.values,
            };
          });
        }
        if (requestTwo.data) {
          requestTwo.data.forEach((affiliation) => {
            this.affiliationsObjects[affiliation.id] = affiliation;
          });
          newFiltersMapped = newFiltersMapped.map((filter) => {
            if (filter.criteria === 'AFFILIATION') {
              return {
                type: filter.type,
                criteria: filter.criteria,
                values: filter.values.map((val) => {
                  return this.affiliationsObjects[val];
                }),
              };
            }
            return {
              type: filter.type,
              criteria: filter.criteria,
              values: filter.values,
            };
          });
        }
        this.filtersJson = newFiltersMapped;
        this.cdr.detectChanges();
      });
    }
  }

  onSubmit() {
    this.hasFormErrors = false;
    const controls = this.salesTeamForm.controls;
    if (this.salesTeamForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      this.selectedTab = 1;
      return;
    }
    this.clicked = true;
    if (this.data && this.data.salesTeam) {
      this.updateSalesTeam(this.prepareSalesTeam());
      return;
    }
    this.addSalesTeam(this.prepareSalesTeam());
  }

  prepareSalesTeam(): SalesTeam {
    const sendFiltersBody = this.filtersJson.map((filter) => {
      if (filter.criteria === 'AFFILIATION' || filter.criteria === 'SPECIALTY') {
        return {
          type: filter.type,
          criteria: filter.criteria,
          values: filter.values.map((val) => {
            return val.id;
          }),
        };
      }
      return filter;
    });
    const controls = this.salesTeamForm.controls;
    const _salesTeam = new SalesTeam();
    _salesTeam.name = controls.name.value;
    _salesTeam.manager_id =
      controls.manager.value && controls.manager.value.id ? controls.manager.value.id : null;
    _salesTeam.type = controls.type.value;
    _salesTeam.calls_on = controls.calls_on.value;
    _salesTeam.is_active = true;
    _salesTeam.members = controls.members.value.map((x) => x.id);
    _salesTeam.filters = sendFiltersBody;
    return _salesTeam;
  }

  addSalesTeam(_salesTeam: SalesTeam) {
    this.salesTeamService.createSalesTeam(_salesTeam).subscribe((res) => {
      if (res) {
        this.layoutUtilsService.showActionNotification(
          'Sales team added successfully',
          MessageType.Create,
          3000,
          false,
          false,
        );
        this.dialogRef.close(true);
      }
    });
  }

  updateSalesTeam(_salesTeam: SalesTeam) {
    _salesTeam.id = this.data && this.data.salesTeam ? this.data.salesTeam.id : '';
    this.salesTeamService.updateSalesTeam(_salesTeam, this.data.salesTeam.id).subscribe((res) => {
      if (res) {
        this.layoutUtilsService.showActionNotification(
          'Sales team updated successfully',
          MessageType.Update,
          3000,
          false,
          false,
        );
        this.dialogRef.close(true);
      }
    });
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.salesTeamForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  close() {
    this.dialogRef.close();
  }

  onOrganizerChange(val) {
    this.salesTeamForm.controls.manager.setValue(val);
    this.salesTeamForm.controls.manager.updateValueAndValidity();
  }

  onMemberChange(val) {
    const users = val.map((item) => {
      if (item && !item.id) {
        return {
          id: item.id,
          name: item.fullName,
        };
      }
      return item;
    });
    this.salesTeamForm.controls.members.setValue(users);
    // this.task.task_responsible_users = users;
    this.salesTeamForm.controls.members.updateValueAndValidity();
  }

  newMemberObject(member: any) {
    return member;
  }

  removeFilter(obj) {
    this.filtersJson = this.filtersJson.filter((item) => item !== obj);
  }

  editFilter(obj) {
    this.salesTeamForm.get('filterType').setValue(obj.type);
    this.salesTeamForm.get('filterCriteria').setValue(obj.criteria);
    this.valueInputType = obj.criteria;
    if (this.valueInputType === 'ZIP_CODE') {
      this.zips = obj.values;
    }
    if (this.valueInputType === 'STATE') {
      this.stateCtrl.setValue(obj.values);
    }
    if (this.valueInputType === 'AFFILIATION') {
      this.affiliationCtrl.setValue(obj.values);
    }
    if (this.valueInputType === 'SPECIALTY') {
      this.specialitiesCtrl.setValue(obj.values);
    }
  }

  filterTypeHint(): string {
    switch (this.salesTeamForm.get('filterType').value) {
      case true:
        return 'Include';
      case false:
        return 'Exclude';
    }
    return 'Exclude';
  }

  addFilter() {
    this.filterIncorrect = false;
    const type = this.salesTeamForm.get('filterType').value ? 'INCLUDE' : 'EXCLUDE';
    const criteria = this.salesTeamForm.get('filterCriteria').value;
    let values = [];
    if (this.valueInputType === 'ZIP_CODE') {
      values = this.zips;
    }
    if (this.valueInputType === 'STATE') {
      values = this.stateCtrl.value;
    }
    if (this.valueInputType === 'AFFILIATION') {
      values = this.affiliationCtrl.value;
    }
    if (this.valueInputType === 'SPECIALTY') {
      values = this.specialitiesCtrl.value;
    }
    if (!type || !criteria || values.length === 0) {
      this.filterIncorrect = true;
      return;
    }
    this.filtersJson.push({
      type,
      criteria,
      values,
    });
    this.salesTeamForm.get('filterType').reset();
    this.salesTeamForm.get('filterCriteria').reset();
    if (this.valueInputType === 'ZIP_CODE') {
      this.zipInput.nativeElement.value = '';
      this.zipValue = null;
      this.zipCtrl.setValue(null);
      this.zips = [];
    }
    if (this.valueInputType === 'STATE') {
      this.stateCtrl.setValue(null);
    }
    if (this.valueInputType === 'AFFILIATION') {
      this.affiliationCtrl.setValue(null);
    }
    if (this.valueInputType === 'SPECIALTY') {
      this.specialitiesCtrl.setValue(null);
    }
  }

  addZip(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    const pattern = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
    if (!pattern.test(value)) {
      this.zipCtrl.setErrors({ pattern: true });
      this.cdr.detectChanges();
      return;
    }

    // Add our zip
    if (value) {
      this.zips.push(value);
    }

    // Clear the input value
    // event.chipInput!.clear();
    this.zipInput.nativeElement.value = '';
    this.zipCtrl.setValue(null);
  }

  removeZip(zip: string): void {
    const index = this.zips.indexOf(zip);

    if (index >= 0) {
      this.zips.splice(index, 1);
    }
  }

  putDash(e) {
    let x = e;
    if (x.length === 6 && x.charAt(5) !== '-') {
      x = x.slice(0, 5) + '-' + x.charAt(x.length - 1);
    }
    return x;
  }

  changeValuesInput($event: MatSelectChange) {
    this.valueInputType = $event.value;
  }

  onAffiliationChange(val) {
    this.affiliationCtrl.setValue(val);
    this.affiliationCtrl.updateValueAndValidity();
  }

  onSpecialtiesChange(val) {
    this.specialitiesCtrl.setValue(val);
    this.specialitiesCtrl.updateValueAndValidity();
  }

  formatNewSpecialtiesFn(val) {
    return val;
  }
}
